import React, { FunctionComponent, useEffect, useState, Suspense, startTransition, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { Route, Routes, useLocation, useParams } from 'react-router-dom';
import { CasinoProps, Game, GameType } from '../games.model';
import { ButtonLinkType } from '../../HeroBannerCarousel';
import RouterAuthGuard from '../../RouteAuthGuard';
import { GameInfo } from '../GameInfo';
import { GameLauncher } from '../GameLauncher';
import { useFetchGames } from '../../../hooks/useFetchGames';
import { transformGameData, transformBingoData } from '../../../utils/games/games.utils';
import { GameGetBySlugQuery } from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/game-get-by-slug.query.generated';
import { BingoGetBySlugQuery } from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/bingo-get-by-slug.query.generated';
import { useSnackbar } from '../../../hooks/snackbar';
import { locale } from '../../../consts';
import { Spinner } from '../../shared/Spinner';
import { useBrandedNavigate } from '../../../hooks/useBrandedNavigate/useBrandedNavigate';
import { useSignIn } from '../../../hooks/auth';
import { SEO } from '../../seo/SEO';

type CasinoRoutesProps = {
  game?: Game;
  isGameInitialised: boolean;
  gameType?: GameType;
  isMiniMode?: boolean;
} & CasinoProps;

/**
 * Route configuration and login handling for Casino SPA.
 */
const CasinoRoutes: FunctionComponent<CasinoRoutesProps> = ({
  game,
  isGameInitialised,
  gameEnabled,
  gameType,
  isMiniMode,
}) => {
  const auth = useAuth();
  const signIn = useSignIn();
  const location = useLocation();
  const isUserAuthenticated = auth?.isAuthenticated;
  const signInTriggeredRef = useRef(false);

  useEffect(() => {
    const normalisedPath = location.pathname.split('?')[0].split('#')[0];
    const isRealRoute = normalisedPath.endsWith('/real');

    if (!isRealRoute) {
      signInTriggeredRef.current = false;

      return;
    }

    if (auth?.isLoading || auth?.activeNavigator) return;

    if (isUserAuthenticated || signInTriggeredRef.current) return;

    signInTriggeredRef.current = true;
    signIn();
  }, [location.pathname, isUserAuthenticated, signIn, auth?.isLoading, auth?.activeNavigator]);

  return (
    <Suspense
      fallback={
        <div className="spinner-wrapper">
          <Spinner />
        </div>
      }
    >
      <SEO title={game?.SEO_title} description={game?.SEO_description}></SEO>
      <Routes>
        <Route
          path="real/*"
          element={
            <RouterAuthGuard redirectTo="/">
              {game && <GameLauncher game={game} mode="real" gameType={gameType} isMiniMode={isMiniMode} />}
            </RouterAuthGuard>
          }
        />
        <Route
          index
          element={
            game && isGameInitialised ? (
              <GameInfo
                game={game}
                gameEnabled={gameEnabled}
                buttonLinkType={ButtonLinkType.GAME}
                isGameInitialised={isGameInitialised}
                isUserAuthenticated={isUserAuthenticated}
              />
            ) : (
              <div className="spinner-wrapper">
                <Spinner />
              </div>
            )
          }
        />
      </Routes>
    </Suspense>
  );
};

/**
 * Casino mini SPA to view & launch a game.
 */
export const Casino: FunctionComponent<CasinoProps & { gameType?: GameType }> = (props) => {
  const { addSnack } = useSnackbar();
  const { navigateHome, navigate } = useBrandedNavigate();
  const { name } = useParams();
  const { gameType = GameType.Game } = props;
  const [game, setGame] = useState<Game | undefined>(undefined);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isMiniMode = searchParams.get('minigame') === 'true';

  useEffect(() => {
    if (!game) return;

    // Normalize pathname by removing query params and hash fragments
    const normalizedPath = location.pathname.split('?')[0].split('#')[0];
    const segments = normalizedPath.split('/').filter(Boolean);
    const [currentBrand] = segments;

    console.log(currentBrand);
    console.log(game?.brand);

    if (currentBrand !== game?.brand) {
      // Reconstruct the path with the correct brand, preserving query params and hash
      const pathWithoutBrand = segments.slice(1).join('/');
      const correctedPath = `/${game.brand}/${pathWithoutBrand}${location.search}${location.hash}`;

      navigate(correctedPath);
    }
  }, [location.pathname, game?.brand, navigate]);

  const handleTransformedData = (transformedData: Game | undefined): void => {
    if (!transformedData) {
      addSnack({
        type: 'error',
        message: `Oops, something's gone wrong`,
        theme: 'dark',
      });
      startTransition(() => {
        navigateHome();
      });

      return;
    }

    setGame(transformedData);
  };

  const handleGameComplete = (data: GameGetBySlugQuery): void => {
    const transformedData = transformGameData(data.game[0]);

    handleTransformedData(transformedData);
  };

  const handleBingoComplete = (data: BingoGetBySlugQuery): void => {
    const transformedData = transformBingoData(data.bingos[0]);

    handleTransformedData(transformedData);
  };

  const { fetchGameBySlug, fetchBingo, fetchedGameBySlugLoading, bingoSlugLoading } = useFetchGames({
    slug: name,
    language: locale,
    onFetchGameBySlugComplete: handleGameComplete,
    onFetchBingoBySlugComplete: handleBingoComplete,
  });

  useEffect(() => {
    (async (): Promise<void> => {
      if (gameType === GameType.Bingo) {
        await fetchBingo();
      } else {
        await fetchGameBySlug();
      }
    })();
  }, [name, gameType]);

  const isLoading = gameType === GameType.Bingo ? bingoSlugLoading : fetchedGameBySlugLoading;

  return (
    <CasinoRoutes {...props} game={game} isGameInitialised={!isLoading} gameType={gameType} isMiniMode={isMiniMode} />
  );
};
